import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, CalendarDays, BadgeCheck, UserRound, Clock } from "lucide-react";
import { ListingCard } from "@/components/ListingCard";
import { Stars } from "@/components/Stars";
import { ShareButtons } from "@/components/ShareButtons";
import { SoldBadge } from "@/components/SoldBadge";
import { FastReplierBadge } from "@/components/TrustBadges";
import { ReportDialog, USER_REASONS } from "@/components/ReportDialog";
import { useAuth } from "@/context/AuthContext";
import { api, timeAgo } from "@/lib/api";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl bg-[#0f2a33] px-5 py-4">
    <p data-testid={testId} className="font-display text-2xl font-extrabold text-[#d4f253]">{value}</p>
    <p className="text-xs text-slate-400">{label}</p>
  </div>
);

export default function SellerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isMe = user && user.id === id;
  const shareUrl = `${window.location.origin}/seller/${id}`;
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/users/${id}/profile`).then((r) => setP(r.data)).catch(() => setErr(true));
    api.get(`/users/${id}/ratings`).then((r) => setReviews(r.data.items)).catch(() => {});
  }, [id]);

  if (err) return <div data-testid="seller-not-found" className="py-32 text-center text-slate-400">Seller not found.</div>;
  if (!p) return <div className="py-32 text-center text-slate-400">Loading…</div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <section className="grain relative overflow-hidden rounded-3xl bg-[#173742] p-6 sm:p-10">
        <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center">
          <div className="flex items-center gap-5">
            <span className="grid h-20 w-20 shrink-0 place-items-center rounded-2xl bg-[#d4f253] font-display text-3xl font-extrabold text-[#0f2a33]">
              {p.name?.[0]?.toUpperCase() || <UserRound />}
            </span>
            <div className="space-y-1.5">
              <h1 data-testid="seller-name" className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{p.name}</h1>
              <div className="flex flex-wrap gap-1.5"><SoldBadge count={p.sold_count} testId="seller-sold-badge" />{p.response?.fast && <FastReplierBadge testId="seller-fast-badge" />}</div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-300">
                {p.rating.count ? <span data-testid="seller-rating" className="flex items-center gap-1.5"><Stars value={p.rating.avg} />{p.rating.avg} · {p.rating.count} review{p.rating.count > 1 ? "s" : ""}</span> : <span data-testid="seller-rating" className="text-slate-400">No ratings yet</span>}
                {p.suburb && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{p.suburb}, {p.state}</span>}
                {p.response?.label && <span data-testid="seller-response" className="flex items-center gap-1 text-[#d4f253]"><Clock className="h-4 w-4" />Usually replies {p.response.label}</span>}
                {p.member_since && <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />Member since {new Date(p.member_since).toLocaleDateString("en-AU", { month: "short", year: "numeric" })}</span>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 lg:ml-auto">
            <Stat label="For sale" value={p.listings.filter((l) => l.status !== "sold").length} testId="seller-stat-active" />
            <Stat label="Sold" value={p.sold_count} testId="seller-stat-sold" />
            <Stat label="Rating" value={p.rating.avg ?? "–"} testId="seller-stat-rating" />
          </div>
        </div>
      </section>

      {p.suspended && <div data-testid="seller-suspended-banner" className="mt-6 rounded-2xl bg-orange-500/15 p-4 text-sm font-bold text-orange-300">This account has been suspended by the ISoldIt team.</div>}

      {!p.suspended && (
        <section className="mt-6 flex flex-col gap-4 rounded-2xl border border-white/10 p-5 md:flex-row md:items-center">
          <p className="text-sm font-semibold md:mr-auto">{isMe ? "Share your profile so friends can see everything you're selling" : `Share ${p.name}'s profile`}</p>
          <ShareButtons url={shareUrl} text={isMe ? "Check out what I'm selling on ISoldIt:" : `Check out ${p.name}'s items on ISoldIt:`} />
        </section>
      )}

      <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_340px]">
        <section>
          <h2 className="mb-6 font-display text-2xl font-bold">Items for sale &amp; sold</h2>
          {p.listings.length ? (
            <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3">{p.listings.map((l, i) => <ListingCard key={l.id} l={l} i={i} />)}</div>
          ) : <div data-testid="seller-no-listings" className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-slate-400">Nothing for sale right now.</div>}
        </section>
        <aside>
          <h2 className="mb-6 flex items-center gap-2 font-display text-2xl font-bold"><BadgeCheck className="h-6 w-6 text-[#d4f253]" />Reviews</h2>
          {reviews.length ? (
            <ul data-testid="seller-reviews" className="space-y-3">
              {reviews.map((r) => (
                <li key={r.id} className="rounded-2xl bg-[#173742] p-4 text-sm">
                  <div className="flex items-center justify-between gap-2"><Stars value={r.stars} size="h-3.5 w-3.5" /><span className="text-xs text-slate-500">{timeAgo(r.created_at)}</span></div>
                  {r.comment && <p className="mt-2 text-slate-200">{r.comment}</p>}
                  <p className="mt-2 text-xs text-slate-400">{r.buyer_name} · bought "{r.listing_title}"</p>
                </li>
              ))}
            </ul>
          ) : <p className="rounded-2xl border border-dashed border-white/15 p-6 text-center text-sm text-slate-400">No reviews yet.</p>}
          {!isMe && (
            <div className="mt-6">
              <ReportDialog user={user} endpoint={`/users/${id}/report`} reasons={USER_REASONS} title={`Report ${p.name}`} label="Report this user" testId="seller-report-btn" onNeedLogin={() => nav(`/login?next=/seller/${id}`)} />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
